<app-shared-action-bar
  [displayActionSectionBar]="true"
  [saveReady]="hasUnsavedChanges && !saving"
  [undoReady]="hasUnsavedChanges && !saving"
  alternativeBackDestination="/settings/events/workflows"
  (saveRequest)="onSaveRequested()"
  (undoRequest)="onUndoRequested()">
</app-shared-action-bar>

@if (!loading && workflow) {
  <div class="container workflow-actions-page p-4">
    <div class="flex items-center justify-between flex-wrap gap-3 mb-4">
      <div>
        <p class="text-xs uppercase tracking-wide text-grey-700 mb-1">Workflow</p>
        <h1 class="text-2xl font-semibold leading-tight">{{workflow.Name}}</h1>
        <p class="text-sm text-grey-800">{{workflow.Description || 'No description provided.'}}</p>
        <p class="text-xs text-grey-600 mt-1">
          <span class="font-semibold">{{actions.length || 0}}</span> {{actionWord(actions.length)}}
        </p>
      </div>
    </div>

    <div class="card shadow-sm">
      <div class="flex items-center justify-between mb-3">
        <h2 class="text-xl font-semibold">Actions</h2>
        @if (actionsDirty) {
          <span class="text-xs text-grey-700">Unsaved changes</span>
        }
      </div>

      <div class="location-card border border-grey-100 rounded px-3 py-2 bg-grey-50 mb-3">
        <div class="flex items-center justify-between flex-wrap gap-2">
          <div class="flex items-center gap-2 flex-wrap">
            <span class="font-semibold text-sm">Locations</span>
            @if (isLocationRestricted) {
              <span class="pill">Restricted ({{selectedLocationCount}})</span>
            } @else {
              <span class="pill">All locations</span>
            }
          </div>
          <div class="flex items-center gap-2">
            <button class="btn-inverse btn-compact btn-small" type="button" (click)="openLocations()">
              <i class="pi pi-map-marker mr-1"></i>
              <span>{{showLocationEditor ? 'Hide' : 'Manage'}}</span>
            </button>
          </div>
        </div>
        @if (locationsLoading) {
          <p class="text-xs text-grey-700 mt-2">Loading locations...</p>
        }
        @if (!locationsLoading && locationsError) {
          <p class="text-xs text-danger-base mt-2">{{locationsError}}</p>
        }
        @if (showLocationEditor && !locationsLoading) {
          <div class="location-editor mt-3">
            <div class="flex gap-3 flex-wrap">
              <label class="flex items-center gap-2">
                <input type="radio" name="locMode" [value]="'all'" [(ngModel)]="restrictMode" (ngModelChange)="onRestrictModeChange($event)">
                <span>All locations</span>
              </label>
              <label class="flex items-center gap-2">
                <input type="radio" name="locMode" [value]="'specific'" [(ngModel)]="restrictMode" (ngModelChange)="onRestrictModeChange($event)">
                <span>Specific locations</span>
              </label>
            </div>
            @if (restrictMode === 'specific') {
              <div class="flex flex-col gap-2 mt-2">
                <p-multiselect
                  [options]="locations"
                  optionLabel="LocationName"
                  optionValue="LocationID"
                  optionDisabled="Disabled"
                  display="chip"
                  [(ngModel)]="selectedLocationIds"
                  defaultLabel="Please Select...">
                </p-multiselect>
                @if (locationsError) {
                  <p class="text-danger-base text-xs">{{locationsError}}</p>
                }
              </div>
            }
            <div class="flex justify-end gap-2 mt-3">
              <button type="button" class="btn-inverse btn-compact btn-small" (click)="closeLocations(true)">Cancel</button>
              <button type="button" class="btn-primary btn-compact btn-small" (click)="saveLocations()" [disabled]="savingLocations">
                <i class="pi" [ngClass]="savingLocations ? 'pi-spin pi-spinner' : 'pi-check'"></i>
                <span class="ml-1">{{savingLocations ? 'Saving...' : 'Save'}}</span>
              </button>
            </div>
          </div>
        }
      </div>

      @if (loadingActions) {
        <p class="text-sm text-grey-800">Loading actions...</p>
      }
      @if (actionsError) {
        <p class="text-sm text-danger-base">{{actionsError}}</p>
      }

      @if (!loadingActions && !actionsError) {
        <div class="flex flex-col gap-3">
          @if (actionsFor().length === 0) {
            <div class="text-sm text-grey-700">No actions yet.</div>
          }

          @for (act of actionsFor(); track trackByAction($index, act)) {
            <div class="action-row border border-grey-100 rounded px-3 py-2 bg-grey-50">
              <div class="flex justify-between gap-2">
                <div class="flex-1">
                  @if (!actionEdits[act.WorkflowActionID]) {
                    <p class="font-semibold text-grey-900">{{act.Title}}</p>
                    @if (act.Description) {
                      <p class="text-sm text-grey-700">{{act.Description}}</p>
                    }
                    <p class="text-xs text-grey-600 mt-1">
                      @if (act.DefaultDueInDays !== undefined && act.DefaultDueInDays !== null) {
                        <span>Due in {{act.DefaultDueInDays}} day(s)</span>
                      }
                      @if (act.IsMandatory) {
                        <span class="mandatory-pill">Mandatory</span>
                      }
                      @if (auditName(act.AuditID)) {
                        <span class="owner-pill">{{ 'audit' | customerTerm }}: {{auditName(act.AuditID)}}</span>
                      }
                      @if (defaultOwnerName(act)) {
                        <span class="owner-pill">Owner: {{defaultOwnerName(act)}}</span>
                      }
                    </p>
                  }
                  @if (actionEdits[act.WorkflowActionID]) {
                    <input class="text-box mb-1" type="text" [(ngModel)]="actionEdits[act.WorkflowActionID].Title" placeholder="Action title" [ngClass]="{'p-invalid': $safeNavigationMigration(editFieldErrors[act.WorkflowActionID]?.title)}">
                    @if (editFieldErrors[act.WorkflowActionID]?.title) {
                      <p class="text-danger-base text-xs mb-1">{{editFieldErrors[act.WorkflowActionID]?.title}}</p>
                    }
                    <textarea class="text-box mb-1" rows="2" [(ngModel)]="actionEdits[act.WorkflowActionID].Description" placeholder="Optional description"></textarea>
                    <div class="action-inline gap-3 mb-1">
                      <div class="field-group">
                        <label class="field-label">Action type</label>
                        <p-select
                          class="prime-input mt-1"
                          [options]="actionVariations"
                          optionLabel="label"
                          optionValue="id"
                          [(ngModel)]="actionEdits[act.WorkflowActionID].ActionVariationID"
                          (ngModelChange)="onEditVariationChange(act.WorkflowActionID, $event)">
                        </p-select>
                      </div>
                    </div>
                    @if (actionEdits[act.WorkflowActionID].ActionVariationID === 2) {
                      <div class="action-inline gap-3 mb-1">
                        <div class="field-group">
                          <label class="field-label">{{ 'audit' | customerTerm }}</label>
                          <p-select
                            class="prime-input mt-1"
                            [options]="auditOptions"
                            optionLabel="AuditName"
                            optionValue="AuditID"
                            optionDisabled="Disabled"
                            [ngModel]="actionEdits[act.WorkflowActionID].AuditID"
                            (ngModelChange)="actionEdits[act.WorkflowActionID].AuditID = $event"
                            [ngClass]="{'p-invalid': $safeNavigationMigration(editFieldErrors[act.WorkflowActionID]?.audit)}">
                          </p-select>
                          @if (editFieldErrors[act.WorkflowActionID]?.audit) {
                            <p class="text-danger-base text-xs mt-1">{{editFieldErrors[act.WorkflowActionID]?.audit}}</p>
                          }
                        </div>
                      </div>
                    }
                    <div class="action-inline gap-3 mb-1">
                      <div class="field-group">
                        <label class="field-label" [for]="'edit-due-' + act.WorkflowActionID">Default due in (days)</label>
                        <input class="text-box" type="number" min="0" [(ngModel)]="actionEdits[act.WorkflowActionID].DefaultDueInDays" [ngClass]="{'p-invalid': $safeNavigationMigration(editFieldErrors[act.WorkflowActionID]?.due)}" [id]="'edit-due-' + act.WorkflowActionID">
                        @if (editFieldErrors[act.WorkflowActionID]?.due) {
                          <p class="text-danger-base text-xs mb-1">{{editFieldErrors[act.WorkflowActionID]?.due}}</p>
                        }
                      </div>
                      <div class="field-group">
                        <label class="field-label">Default owner</label>
                        <p-select
                          class="prime-input mt-1"
                          [options]="users"
                          optionLabel="DisplayName"
                          optionDisabled="Disabled"
                          [(ngModel)]="actionEditAssignees[act.WorkflowActionID]"
                          [ngClass]="{'p-invalid': $safeNavigationMigration(editFieldErrors[act.WorkflowActionID]?.owner)}">
                          <ng-template let-user pTemplate="item">
                            <span [ngClass]="{'font-bold': user?.UserTypeID === 2}">{{user?.DisplayName}}</span>
                          </ng-template>
                          <ng-template let-user pTemplate="selectedItem">
                            <span [ngClass]="{'font-bold': user?.UserTypeID === 2}">{{user?.DisplayName}}</span>
                          </ng-template>
                        </p-select>
                        @if (editFieldErrors[act.WorkflowActionID]?.owner) {
                          <p class="text-danger-base text-xs mt-1">{{editFieldErrors[act.WorkflowActionID]?.owner}}</p>
                        }
                      </div>
                      <div class="field-group checkbox-group">
                        <div class="checkbox-inline">
                          <input type="checkbox" [(ngModel)]="actionEdits[act.WorkflowActionID].IsMandatory" id="edit-mandatory-{{act.WorkflowActionID}}">
                          <span class="text-sm">Mandatory</span>
                        </div>
                      </div>
                    </div>
                    <div class="flex gap-2 action-buttons">
                      <button class="btn-primary btn-compact btn-small" type="button" (click)="confirmEdit(act.WorkflowActionID)">
                        <i class="pi pi-check"></i>
                        <span class="ml-1">Apply</span>
                      </button>
                      <button class="btn-inverse btn-compact btn-small" type="button" (click)="cancelEdit(act.WorkflowActionID)">
                        <i class="pi pi-times"></i>
                        <span class="ml-1">Cancel</span>
                      </button>
                    </div>
                  }
                </div>
                <div class="flex flex-col items-end gap-2">
                  @if (!actionEdits[act.WorkflowActionID]) {
                    <div class="flex gap-2">
                      <button class="btn-inverse btn-compact workflow-action-btn" type="button" (click)="startEdit(act)">
                        <i class="pi pi-pencil"></i>
                      </button>
                      <button class="btn-negative btn-compact workflow-action-btn" type="button" pConfirmPopup (click)="confirmDeleteAction($event, act.WorkflowActionID)">
                        <i class="pi pi-trash"></i>
                      </button>
                    </div>
                  }
                </div>
              </div>
            </div>
          }

          <div class="add-action-form border border-grey-200 rounded px-3 py-3 bg-white mt-1">
            <p class="font-semibold text-sm mb-2">Add action</p>
            <input class="text-box w-full mb-1" type="text" [(ngModel)]="actionForm.Title" placeholder="Action title" [ngClass]="{'p-invalid': actionFieldErrors.title}">
            @if (actionFieldErrors.title) {
              <p class="text-danger-base text-xs mb-1">{{actionFieldErrors.title}}</p>
            }
            <textarea class="text-box w-full mb-2" rows="3" [(ngModel)]="actionForm.Description" placeholder="Optional description"></textarea>
            <div class="action-inline gap-3 mb-2">
              <div class="field-group">
                <label class="field-label">Action type</label>
                <p-select
                  class="prime-input mt-1"
                  [options]="actionVariations"
                  optionLabel="label"
                  optionValue="id"
                  [(ngModel)]="actionForm.ActionVariationID"
                  (ngModelChange)="onAddVariationChange($event)">
                </p-select>
              </div>
            </div>
            @if (actionForm.ActionVariationID === 2) {
              <div class="action-inline gap-3 mb-2">
                <div class="field-group">
                  <label class="field-label">{{ 'audit' | customerTerm }}</label>
                  <p-select
                    class="prime-input mt-1"
                    [options]="auditOptions"
                    optionLabel="AuditName"
                    optionValue="AuditID"
                    optionDisabled="Disabled"
                    [(ngModel)]="actionForm.AuditID"
                    [ngClass]="{'p-invalid': actionFieldErrors.audit}">
                  </p-select>
                  @if (actionFieldErrors.audit) {
                    <p class="text-danger-base text-xs mb-1">{{actionFieldErrors.audit}}</p>
                  }
                </div>
              </div>
            }
            <div class="action-inline gap-3 mb-2">
              <div class="field-group">
                <label class="field-label" for="add-due-single">Default due in (days)</label>
                <input class="text-box w-full" id="add-due-single" type="number" min="0" [(ngModel)]="actionForm.DefaultDueInDays" placeholder="e.g. 7" [ngClass]="{'p-invalid': actionFieldErrors.due}">
                @if (actionFieldErrors.due) {
                  <p class="text-danger-base text-xs mb-1">{{actionFieldErrors.due}}</p>
                }
              </div>
            </div>
            <div class="action-inline gap-3 mb-2">
              <div class="field-group">
                <label class="field-label">Default owner</label>
                <p-select
                  class="w-full prime-input mt-1"
                  [options]="users"
                  optionLabel="DisplayName"
                  optionDisabled="Disabled"
                  [(ngModel)]="actionAssignee"
                  placeholder="Select a user"
                  [ngClass]="{'p-invalid': actionFieldErrors.owner}">
                  <ng-template let-user pTemplate="item">
                    <span [ngClass]="{'font-bold': user?.UserTypeID === 2}">{{user?.DisplayName}}</span>
                  </ng-template>
                  <ng-template let-user pTemplate="selectedItem">
                    <span [ngClass]="{'font-bold': user?.UserTypeID === 2}">{{user?.DisplayName}}</span>
                  </ng-template>
                </p-select>
                @if (actionFieldErrors.owner) {
                  <p class="text-danger-base text-xs mb-1">{{actionFieldErrors.owner}}</p>
                }
              </div>
            </div>
            <div class="action-inline gap-3 mb-2">
              <div class="field-group checkbox-group inline-checkbox">
                <div class="checkbox-inline">
                  <input type="checkbox" [(ngModel)]="actionForm.IsMandatory" id="mandatory-single">
                  <span class="text-sm">Mandatory</span>
                </div>
              </div>
            </div>
            <div class="flex justify-end action-buttons">
              <button class="btn-primary btn-small" type="button" (click)="addWorkflowAction()" [disabled]="saving">
                <i class="pi" [ngClass]="saving ? 'pi-spin pi-spinner' : 'pi-plus'"></i>
                <span class="ml-1">{{saving ? 'Adding...' : 'Add Action'}}</span>
              </button>
            </div>
          </div>
        </div>
      }
    </div>
  </div>
}

@if (loading) {
  <p class="text-sm text-grey-800 p-4">Loading workflow...</p>
}
@if (!loading && errorMessage) {
  <p class="text-sm text-danger-base p-4">{{errorMessage}}</p>
}
